import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { unifiedApiService } from '../../services/unified';
import { queryKeys } from '../../lib/queryClient';
import type {
  ConversationMessage,
  ConversationInput,
  ConversationWithMetadata,
  EnhancedConversationMessage,
  MessageSearchRequest,
  MessageSearchResult,
} from '../../services/unified/types';

/**
 * Query hook for getting user conversations
 */
export const useConversations = () => {
  return useQuery({
    queryKey: queryKeys.messages.conversations(),
    queryFn: (): Promise<ConversationMessage[]> => {
      return unifiedApiService.messaging.getConversations();
    },
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Query hook for getting conversation details
 */
export const useConversationDetails = (id: string) => {
  return useQuery({
    queryKey: [...queryKeys.messages.conversation(id), 'details'],
    queryFn: (): Promise<ConversationWithMetadata> => {
      return unifiedApiService.messaging.getConversationDetails(id);
    },
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Query hook for getting conversation messages
 */
export const useConversationMessages = (id: string, page?: number, limit?: number) => {
  return useQuery({
    queryKey: queryKeys.messages.thread(id),
    queryFn: (): Promise<EnhancedConversationMessage[]> => {
      return unifiedApiService.messaging.getConversationMessages(id, page, limit);
    },
    enabled: !!id,
    staleTime: 30 * 1000, // 30 seconds
  });
};

/**
 * Query hook for searching messages
 */
export const useSearchMessages = (request: MessageSearchRequest) => {
  return useQuery({
    queryKey: [...queryKeys.messages.all, 'search', request],
    queryFn: (): Promise<MessageSearchResult> => {
      return unifiedApiService.messaging.searchMessages(request);
    },
    enabled: !!request.query && request.query.length > 2, // Only search if query is at least 3 characters
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Query hook for getting admin conversations for a user
 */
export const useAdminConversations = (userId: string) => {
  return useQuery({
    queryKey: [...queryKeys.admin.all, 'conversations', userId],
    queryFn: (): Promise<ConversationMessage[]> => {
      return unifiedApiService.messaging.getAdminConversations(userId);
    },
    enabled: !!userId,
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Mutation hook for sending a message
 */
export const useSendMessage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: ConversationInput): Promise<void> => {
      await unifiedApiService.messaging.sendMessage(data);
    },
    onSuccess: () => {
      // Invalidate conversations list
      queryClient.invalidateQueries({ queryKey: queryKeys.messages.conversations() });
      // Invalidate all conversation threads
      queryClient.invalidateQueries({ queryKey: queryKeys.messages.all });
    },
  });
};

/**
 * Mutation hook for sending a message with attachments
 */
export const useSendMessageWithAttachments = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (data: {
      message: string;
      recipient_id?: string;
      conversation_id?: string;
      attachments?: File[];
    }): Promise<void> => {
      await unifiedApiService.messaging.sendMessageWithAttachments(data);
    },
    onSuccess: () => {
      // Invalidate conversations list
      queryClient.invalidateQueries({ queryKey: queryKeys.messages.conversations() });
      // Invalidate all conversation threads
      queryClient.invalidateQueries({ queryKey: queryKeys.messages.all });
    },
  });
};

/**
 * Mutation hook for marking a message as read
 */
export const useMarkMessageRead = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await unifiedApiService.messaging.markMessageRead(id);
    },
    onSuccess: () => {
      // Invalidate conversations to update unread counts
      queryClient.invalidateQueries({ queryKey: queryKeys.messages.conversations() });
    },
  });
};

/**
 * Mutation hook for marking a conversation as read
 */
export const useMarkConversationRead = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (): Promise<{ message: string; messages_marked: number }> => {
      return unifiedApiService.messaging.markConversationRead();
    },
    onSuccess: () => {
      // Invalidate conversations list
      queryClient.invalidateQueries({ queryKey: queryKeys.messages.conversations() });
    },
  });
};

/**
 * Mutation hook for admin sending a message
 */
export const useSendAdminMessage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ userId, data }: { userId: string; data: ConversationInput }): Promise<void> => {
      await unifiedApiService.messaging.sendAdminMessage(userId, data);
    },
    onSuccess: (_, { userId }) => {
      // Invalidate admin conversations for this user
      queryClient.invalidateQueries({ queryKey: [...queryKeys.admin.all, 'conversations', userId] });
      // Invalidate all conversations
      queryClient.invalidateQueries({ queryKey: queryKeys.messages.all });
    },
  });
};

/**
 * Mutation hook for downloading an attachment
 */
export const useDownloadAttachment = () => {
  return useMutation({
    mutationFn: async ({ filename, displayName }: { filename: string; displayName?: string }): Promise<Blob> => {
      const blob = await unifiedApiService.messaging.getAttachment(filename);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = displayName || filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      return blob;
    },
    onError: (error) => {
      console.error('Attachment download failed:', error);
    },
  });
};
